import { dataSlice as createDataSLice } from '../../../core/data';
import { sliceList as createListSlice } from '../../../core/list';
import { sliceFilter as createFilterSlice } from '../../../core/filter';
import { detailSlice as createDetailSlice } from '../../../core/detail';
import { sliceSearch as createSearchSlice } from '../../../core/search';
import {
  combineReducers,
  createAction,
  createSelector,
} from '@reduxjs/toolkit';
import _ from 'lodash';

import { ListConf } from '../conf/List.conf';
import { SearchConf } from '../conf/Search.conf';
import { FilterConf } from '../../operations/conf/Filter.conf';
import { DataConf } from '../../operations/conf/Data.conf';
import { DetailConf } from '../../operations/conf/Detail.conf';
import { getDefaultOptionLocation } from '../../marina/conf/data';
import { QueryConf } from '../../conf/Query.conf';

export const tag = 'depot';

// REDUCERS
const dataSlice = createDataSLice(`${tag}/data`, DataConf.initialState);
const listSlice = createListSlice(`${tag}/list`, ListConf.initialState);
const filterSlice = createFilterSlice(`${tag}/filter`, FilterConf.initialState);
const searchSlice = createSearchSlice(`${tag}/search`, SearchConf.initialState);
const detailSlice = createDetailSlice(`${tag}/detail`, DetailConf.initialState);

export const reducer = combineReducers({
  search: searchSlice.reducer,
  data: dataSlice.reducer,
  list: listSlice.reducer,
  filter: filterSlice.reducer,
  detail: detailSlice.reducer,
});

// ACTIONS
export const actions = {
  fetchAllAction: createAction(`${tag}/fetchAll`),
  fetchDetailAction: createAction(`${tag}/fetchOne`),
  ...searchSlice.actions,
  ...dataSlice.actions,
  ...listSlice.actions,
  ...filterSlice.actions,
  ...detailSlice.actions,
};

// QUERY
export const querySelector = createSelector(
  (state) => state[tag].list,
  (state) => state[tag].filter,
  (state) => state[tag].search,
  (list, filter, search) =>
    QueryConf.get(list, filter, search, FilterConf, SearchConf)
);

// LIST
export const listSelector = createSelector(
  (state) => state[tag].data,
  (state) => state[tag].list,
  ({ data, meta }, { pagination, sort }) => ({
    data,
    meta,
    pagination,
    sort,
  })
);

// DETAIL
export const detailSelector = createSelector(
  (state) => state[tag].detail,
  (detail) => detail
);

// EDIT
export const editSelector = createSelector(
  (state) => state[tag].detail,
  ({ data, loading, error }) => {
    const yacht = _.get(data, 'attributes.id_yacht.data');
    const supplier = _.get(data, 'attributes.id_supplier.data');
    const mexal_person_in_charge = _.get(
      data,
      'attributes.id_mexal_person_in_charge.data'
    );
    const department = _.get(data, 'attributes.id_department.data');
    const marina = _.get(data, 'attributes.id_marina.data');
    const status_operation_BC = _.get(data, 'attributes.status_operation_BC');
    const status_operation_BF = _.get(data, 'attributes.status_operation_BF');
    const status_operation_booking = _.get(
      data,
      'attributes.status_operation_booking'
    );
    const status_billing = _.get(data, 'attributes.status_billing');
    const billing_notes = _.get(data, 'attributes.billing_notes');
    const account_type = _.get(data, 'attributes.account_type');
    const reservation = _.get(data, 'attributes.id_reservation.data');
    const driver_in_charge = _.get(data, 'attributes.id_driver_in_charge.data');
    const id_user_in_charge = _.get(data, 'attributes.id_user_in_charge.data');
    const id_agent_NA = _.get(
      data,
      'attributes.id_yacht.data.attributes.id_attendance.data[0].attributes.id_agent_NA.data.attributes.id_users_permissions_user.data.attributes'
    );
    const id_service = _.get(data, 'attributes.id_service.data');

    const mexal_validation = _.get(
      data,
      'attributes.id_yacht.data.attributes.id_attendance.data[0].attributes.mexal_validation'
    );

    const type_of_contact = _.get(data, 'attributes.type_of_contact');
    return {
      loading,
      error,
      data: data
        ? {
          id_agent_NA: id_agent_NA
            ? {
              label: `${id_agent_NA.name} ${id_agent_NA.surname}`,
              id: id_agent_NA.name,
            }
            : null,
          id_user_in_charge: id_user_in_charge
            ? {
              name: `${id_user_in_charge.attributes.id_users_permissions_user.data.attributes.name} ${id_user_in_charge.attributes.id_users_permissions_user.data.attributes.surname}`,
              id: id_user_in_charge.id,
            }
            : null,
          id_driver_in_charge: driver_in_charge
            ? {
              name: driver_in_charge.attributes.username_mexal,
              id: driver_in_charge.id,
            }
            : null,
          status_operation_booking: status_operation_booking,
          id_reservation: reservation
            ? {
              name: reservation.attributes.reservation_type,
              id: reservation.id,
            }
            : null,
          id_yacht: yacht
            ? {
              name: `${yacht?.attributes?.yacht_name} #${yacht?.id}`,
              id: yacht?.id,
              has_mexal: mexal_validation,
            }
            : null,

          id_mexal_person_in_charge: mexal_person_in_charge
            ? {
              name: `${mexal_person_in_charge.attributes.id_users_permissions_user.data.attributes.name} ${mexal_person_in_charge.attributes.id_users_permissions_user.data.attributes.surname}`,
              id: mexal_person_in_charge.id,
            }
            : null,

          id_department: department.id,
          operation_service: _.get(data, 'attributes.operation_service'),

          requested_date: _.get(data, 'attributes.requested_date'),
          requested_hour: _.get(data, 'attributes.requested_hour') || null,
          service_detail: _.get(data, 'attributes.service_detail') || '',
          OC_BC_number: _.get(data, 'attributes.OC_BC_number') || '',
          OF_BF_number: _.get(data, 'attributes.OF_BF_number') || '',
          OC_fees: _.get(data, 'attributes.OC_fees') || '',
          id_service: id_service
            ? { name: id_service.attributes.service, id: id_service.id }
            : null,
          operation_date: _.get(data, 'attributes.operation_date'),
          status_operation_BC: status_operation_BC
            ? { label: status_operation_BC, id: status_operation_BC }
            : null,
          status_operation_BF: status_operation_BF
            ? { label: status_operation_BF, id: status_operation_BF }
            : null,
          status_billing,
          billing_notes,
          selling_price: _.get(data, 'attributes.selling_price'),
          purchase_price: _.get(data, 'attributes.purchase_price'),
          purchase_date: _.get(data, 'attributes.purchase_date'),
          account_type: account_type
            ? { label: account_type, id: account_type }
            : null,
          id_supplier: supplier
            ? { name: `${supplier.attributes.mexal_code} - ${supplier.attributes.company_name}`, id: supplier.id }
            : null,
          id_marina: marina ? getDefaultOptionLocation(marina) : null,
          mexalValidation: mexal_validation,
          is_cagliari: _.get(data, 'attributes.is_cagliari')
            ? _.get(data, 'attributes.is_cagliari')
            : false,
          note: _.get(data, 'attributes.note') || '',
          contact: _.get(data, 'attributes.contact') || '',
          type_of_contact: type_of_contact
            ? { label: type_of_contact, id: type_of_contact }
            : null,
          yacht_name: yacht
            ? `${yacht?.attributes?.yacht_name} #${yacht?.id}`
            : '',
          agent_NA_name: id_agent_NA
            ? `${id_agent_NA.name} ${id_agent_NA.surname}`
            : '',
        }
        : null,
    };
  }
);
